import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  model,
  Output,
  signal,
} from "@angular/core";
import { ApiService } from "../../shared/services/api.service";
import {
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { Observable, of } from "rxjs";
import { catchError, debounceTime } from "rxjs/operators";
import { empty } from "../../functions";
import {
  alertMessages,
  AlertService,
} from "../../shared/services/alert.service";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { CommonModule } from "@angular/common";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { TranslateModule } from "@ngx-translate/core";
const noop = () => {};

export const TYPEAHEAD_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TypeaheadComponent),
  multi: true,
};

@Component({
  selector: "[app-typeahead]",
  templateUrl: "./typeahead.component.html",
  styleUrls: ["./typeahead.component.scss"],
  providers: [TYPEAHEAD_INPUT_CONTROL_VALUE_ACCESSOR],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class TypeaheadComponent {
  debounceTime = 500;

  // Icons
  faSpinner = faSpinner;

  searchControl: UntypedFormControl;

  /**
   * Inputs
   */
  @Input() selectorName = "name";
  @Input() selectorValue = "id";

  @Input() label = "";

  @Input() apiEndpoint = "";
  data: any = model([]);
  @Input() hasPagination = true;
  @Input() pageSize = 20;

  // Emitter
  @Output() selectEvent: EventEmitter<any> = new EventEmitter<any>();

  showDropdown = signal(false);
  isLoading = signal(false);

  /**
   * Ng typeahead properties
   */

  public model: any;

  /**
   * Control accessors
   */

  // The internal data model
  private innerValue: any = "";

  // Placeholders for the callbacks which are later provided
  // by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  /**
   * Constructor
   *
   * @param api
   * @param alertService
   */
  constructor(private api: ApiService, private alertService: AlertService) {
    this.searchControl = new UntypedFormControl();

    // Get data with debounce time
    this.searchControl.valueChanges
      .pipe(debounceTime(this.debounceTime))
      .subscribe((result) => {
        this.value = result;

        // Reload data if input value changed
        if (!empty(this.value)) {
          this.getData();
        }
      });
  }

  /**
   * Close dropdown
   *
   * @param $timeout
   */
  closeDropdown($timeout: number = 0) {
    setTimeout(() => {
      this.showDropdown.set(false);
    }, $timeout);
  }

  /**
   * Get data
   */
  getData() {
    if (empty(this.apiEndpoint)) return;
    if (this.isLoading()) return;

    this.isLoading.set(true);

    let queryParams = null;

    if (this.hasPagination) {
      queryParams = {
        PageNumber: 1,
        PageSize: this.pageSize,
        SearchTerm: this.innerValue,
      };
    }

    this.api.setEndpoint(this.apiEndpoint);

    this.api
      .GET(queryParams)
      .pipe(catchError(this.handleError("apiGET", false)))
      .subscribe((result: any) => {
        if (result) {
          const collection: any[] = [];

          // Create custom typeahead collection of requested data
          if (result.hasOwnProperty("data")) {
            for (const i in result.data) {
              const row = result.data[i];

              const newRow = {
                value: row[this.selectorValue],
                name: row[this.selectorName],
                data: row,
              };

              collection.push(newRow);
            }
          }

          this.data.update(() => Object.assign([], collection));
          this.isLoading.set(false);
          this.showDropdown.set(true);
        } else {
          this.isLoading.set(false);
          this.showDropdown.set(false);
        }
      });
  }

  /**
   * On select event handler
   *
   * @param $event
   */
  onSelect($event: any) {
    // Set value in input
    if ($event.hasOwnProperty("data")) {
      if ($event["data"].hasOwnProperty(this.selectorValue)) {
        this.value = $event["data"][this.selectorValue];
        this.searchControl.setValue($event["data"][this.selectorName], {
          emitEvent: false,
        });
      }
    }

    this.selectEvent.emit($event);
    this.showDropdown.set(false);
  }

  // get accessor
  get value(): any {
    return this.innerValue;
  }

  // set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  // Set touched on blur
  onBlur() {
    this.onTouchedCallback();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    this.innerValue = value;
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  /**
   * Error handler
   *
   * @param operation
   * @param result
   */
  handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      // Handle given operation
      switch (operation) {
        case "apiGET":
          // do something
          break;

        default:
          this.alertService.errorWithReport(error, `typeahead:${operation}`);
          break;
      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
